import React, { useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { ButtonLoading, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { CustomValidationConfig, CustomValidationConstraint } from "go-form/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { apiOrganization } from "../../../../../../../../../services/Api/Organization/apiOrganization";
import { ApplicationApi } from "../../../../../../../../../services/Api/Organization/types";
import { AppState } from "../../../utils/types";
import { parseApplicationSettings, parseApplicationSettingsValues } from "../../../utils/utils";

interface Props {
	show: boolean;
	onHide: () => void;
	appState: AppState;
}

const HardRockCafeInstallModal = ({ show, onHide, appState }: Props) => {
	const { t } = useTranslation();
	const form = useForm<ApplicationApi>({
		criteriaMode: "all",
		defaultValues: {
			...appState.app,
			settings: {
				...appState.app.settings,
				...parseApplicationSettingsValues(appState.app.settings || {}),
			},
		},
	});
	const {
		register,
		handleSubmit,
		formState: { errors },
		formState,
		setError,
	} = form;
	const [loading, setLoading] = useState(false);
	const { addSuccessFlash, addFlash } = useFlash();
	const history = useHistory();
	const organization = useSelector(selectOrganization);
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);

	const textInputValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
			utils: { watch: form.watch },
		}),
		[setErrors, form.watch]
	) satisfies CustomValidationConfig;

	const onSubmit = handleSubmit(async (data: ApplicationApi) => {
		if (!validateCustomErrors()) return;

		setLoading(true);
		const params: Record<string, any> = { include: "settings" };

		const settings = parseApplicationSettings(data.settings);
		const newData = {
			name: data.name,
			id: appState.app.id,
			settings,
		};

		try {
			if (appState.app.id) {
				await apiOrganization.updateApp(newData, params);
				addSuccessFlash(t("lib:common.flash.saved"));
				onHide();
			} else {
				const res = await apiOrganization.installApps("HARD_ROCK_CAFE", newData, params);
				addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
				history.push(`/${organization.id}/apps/active/${res.id}`);
			}
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	return (
		<Modal onHide={onHide} show={show}>
			<FormDirty formState={formState} onSubmit={onSubmit}>
				<Modal.Header closeButton>
					<Modal.Title>
						{t("modules.app.field.install.title")} {t("enums.apps.providers.HARD_ROCK_CAFE")}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<FormInput
						name="settings.terminal_id"
						errors={errors}
						register={register}
						label={t("modules.app.field.terminal_id.title")}
						customValidationConfig={textInputValidationConfig}
					/>
					<FormInput
						name="settings.location_id"
						errors={errors}
						register={register}
						label={t("modules.app.field.location_id.title")}
						customValidationConfig={textInputValidationConfig}
					/>
					<FormInput
						name="settings.security_guid"
						errors={errors}
						register={register}
						label={t("modules.app.field.security_guid.title")}
						customValidationConfig={textInputValidationConfig}
					/>
				</Modal.Body>
				<Modal.Footer>
					<ButtonLoading loading={loading} type="submit" onClick={onSubmit}>
						{t("lib:common.action.install")}
					</ButtonLoading>
					<Button variant="light" onClick={onHide}>
						{t("lib:common.action.cancel")}
					</Button>
				</Modal.Footer>
			</FormDirty>
		</Modal>
	);
};

export default HardRockCafeInstallModal;
